/**
 * Cart Mark ID Sync
 * Enriches cart lines missing _mark_id / _mark_name from product classification metafields.
 * Covers BOGOS free gifts, Quick Add paths, and any other add-to-cart flow that omits mark fields.
 */

/** @type {Map<string, { mark_id?: string, mark_name?: string }>} */
const markMetaCache = new Map();

/**
 * @param {object} item
 * @returns {boolean}
 */
function itemNeedsMarkIdSync(item) {
  const props = item.properties || {};
  const currentMarkId = props._mark_id != null ? String(props._mark_id).trim() : '';
  return currentMarkId === '' || currentMarkId === '0';
}

/**
 * @param {string} handle
 * @param {number|string} [variantId]
 * @returns {Promise<{ mark_id?: string, mark_name?: string } | null>}
 */
async function fetchProductMarkMeta(handle, variantId) {
  if (!handle) return null;

  const cacheKey = `${handle}:${variantId ?? ''}`;
  if (markMetaCache.has(cacheKey)) return markMetaCache.get(cacheKey) ?? null;

  const root = window.Shopify?.routes?.root || '/';
  const params = new URLSearchParams({ view: 'mark-meta' });
  if (variantId != null && String(variantId).trim() !== '') {
    params.set('variant', String(variantId));
  }

  try {
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}?${params.toString()}`);
    if (!response.ok) return null;

    const data = await response.json();
    const normalized = {
      mark_id: data?.mark_id != null ? String(data.mark_id).trim() : '',
      mark_name: data?.mark_name != null ? String(data.mark_name).trim() : '',
    };
    markMetaCache.set(cacheKey, normalized);
    return normalized;
  } catch (error) {
    console.error('CartMarkIdSync: failed to fetch mark meta', handle, error);
    return null;
  }
}

/**
 * SSR cart size from layout/theme.liquid. Null if the attribute is missing (fallback to /cart.js).
 * @returns {number | null}
 */
function getRenderedCartItemCount() {
  const raw = document.documentElement?.getAttribute('data-cart-item-count');
  if (raw == null || raw === '') return null;
  const count = Number.parseInt(raw, 10);
  return Number.isFinite(count) ? count : null;
}

window.syncMarkIdPropertiesAfterCartChange = async function syncMarkIdPropertiesAfterCartChange() {
  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return false;

    const cart = await response.json();
    if (!cart?.items?.length) return false;

    const linesNeedingSync = cart.items
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => itemNeedsMarkIdSync(item));

    if (!linesNeedingSync.length) return false;

    const markMetaResults = await Promise.all(
      linesNeedingSync.map(({ item }) => fetchProductMarkMeta(item.handle, item.variant_id))
    );

    const changeRequests = [];

    linesNeedingSync.forEach(({ item, line }, resultIndex) => {
      const markMeta = markMetaResults[resultIndex];
      if (!markMeta?.mark_id) return;

      const currentMarkId = item.properties?._mark_id != null ? String(item.properties._mark_id).trim() : '';
      const currentMarkName = item.properties?._mark_name != null ? String(item.properties._mark_name).trim() : '';
      const nextMarkId = String(markMeta.mark_id).trim();
      const nextMarkName = markMeta.mark_name ? String(markMeta.mark_name).trim() : '';

      const markIdChanged = nextMarkId !== '' && nextMarkId !== currentMarkId;
      const markNameChanged = nextMarkName !== '' && nextMarkName !== currentMarkName;
      const needsItemSource = !item.properties?._itemSource && !item.properties?.itemSource;

      if (!markIdChanged && !markNameChanged && !needsItemSource) return;

      const properties = { ...(item.properties || {}) };
      if (markIdChanged) properties._mark_id = nextMarkId;
      if (markNameChanged) properties._mark_name = nextMarkName;
      if (needsItemSource) properties._itemSource = 'FPC';

      changeRequests.push({
        line,
        quantity: item.quantity,
        properties,
      });
    });

    if (!changeRequests.length) return false;

    for (const req of changeRequests) {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
      });
      if (!res.ok) {
        console.error('CartMarkIdSync: cart/change.js failed', await res.text(), req);
      }
    }

    document.dispatchEvent(new CustomEvent('cart:mark-id-synced', { bubbles: true }));
    return true;
  } catch (error) {
    console.error('Error in window.syncMarkIdPropertiesAfterCartChange:', error);
  }

  return false;
};

/** @type {boolean} */
let markIdSyncMicrotaskPending = false;

function requestMarkIdSyncAfterCartActivity() {
  if (markIdSyncMicrotaskPending) return;
  markIdSyncMicrotaskPending = true;
  queueMicrotask(async () => {
    markIdSyncMicrotaskPending = false;
    try {
      await window.syncMarkIdPropertiesAfterCartChange();
    } catch (_) {
      // Errors logged inside sync
    }
  });
}

/**
 * @param {RequestInfo|URL|string} input
 * @returns {string}
 */
function resolveFetchUrl(input) {
  if (typeof input === 'string') return input;
  if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  if (input && typeof input === 'object' && 'url' in input) return String(/** @type {{ url: string }} */ (input).url);
  return '';
}

function setupMarkIdCartActivityHooks() {
  document.addEventListener('cart:update', requestMarkIdSyncAfterCartActivity);
  document.addEventListener('cart:updated', requestMarkIdSyncAfterCartActivity);

  if (typeof window.fetch !== 'function') return;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async function markIdCartFetchHook(input, init) {
    const response = await originalFetch(input, init);
    try {
      const url = resolveFetchUrl(input);
      if (
        response.ok &&
        (url.includes('/cart/add') || url.includes('/cart/update') || url.includes('/cart/change'))
      ) {
        void window.syncMarkIdPropertiesAfterCartChange();
      }
    } catch (_) {
      // Never break callers of fetch
    }
    return response;
  };
}

document.addEventListener('DOMContentLoaded', () => {
  // IW-AUC-09: empty bag already known from Liquid — do not GET /cart.js on every page.
  if (getRenderedCartItemCount() === 0) return;
  void window.syncMarkIdPropertiesAfterCartChange();
});

setupMarkIdCartActivityHooks();
